import { currentUser, getBuildConfig, parseFileUrl, pointer, pointerId, queryClass, type ParseRecord } from './parse';

export type AppBranding = { name: string; logoUrl?: string };

export type LearnerProfile = {
  name: string;
  initials: string;
  phone: string;
  email: string;
  courseTitle: string;
  avatarUrl?: string;
};

const ayurguideLogoUrl = '/ayurguide-logo.png';

export const defaultAppBranding: AppBranding = { name: 'AyurGuide', logoUrl: ayurguideLogoUrl };
export const defaultLearnerProfile: LearnerProfile = { name: 'Learner', initials: 'L', phone: '', email: '', courseTitle: 'Learning workspace' };

let brandingPromise: Promise<AppBranding> | undefined;
let profileCache: { userId?: string; value: LearnerProfile } | undefined;
let profilePromise: Promise<LearnerProfile> | undefined;

function textValue(...values: unknown[]) {
  return values.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';
}

export function profileInitials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'L';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

function currentUserId() {
  return pointerId((currentUser() as ParseRecord | null)?.objectId);
}

export async function loadAppBranding(): Promise<AppBranding> {
  if (!brandingPromise) {
    brandingPromise = getBuildConfig().then((config) => ({
      name: textValue(config.MobAppName, config.AppName) || defaultAppBranding.name,
      logoUrl: ayurguideLogoUrl,
    })).catch(() => defaultAppBranding);
  }
  return brandingPromise;
}

export async function loadLearnerProfile(): Promise<LearnerProfile> {
  const userId = currentUserId();
  const cachedProfile = profileCache;
  if (cachedProfile && cachedProfile.userId === userId) return cachedProfile.value;
  if (profilePromise) return profilePromise;

  profilePromise = (async () => {
    const user = currentUser() as ParseRecord | null;
    if (!user || !userId) return defaultLearnerProfile;

    let customer: ParseRecord = {} as ParseRecord;
    try {
      const rows = await queryClass('elearning_Customer', {
        include: ['CoursePtr'],
        keys: ['First_name', 'Last_name', 'Name', 'Email_id', 'Mob_no', 'UserId', 'CoursePtr', 'ProfileImage', 'ProfileImg', 'Image', 'Photo', 'Avatar'],
        limit: 1,
        where: (query) => query.equalTo('UserId', pointer('_User', userId)),
      });
      customer = rows[0] ?? customer;
    } catch {
      // Keep the shell usable when the legacy customer record is incomplete.
    }

    const name = textValue(
      [customer.First_name, customer.Last_name].filter(Boolean).join(' '),
      customer.Name,
      user.name,
      [user.firstName, user.lastName].filter(Boolean).join(' '),
      user.username,
      user.email,
    ) || defaultLearnerProfile.name;
    let coursePointer = customer.CoursePtr;
    if (!coursePointer && customer.objectId) {
      try {
        const subscriptions = await queryClass('elearning_Subscriptions', {
          include: ['CoursePtr'],
          keys: ['CoursePtr', 'CustomerPtr', 'StartDate', 'EndDate', 'TxnStatus'],
          limit: 1,
          order: '-updatedAt',
          where: (query) => query.equalTo('CustomerPtr', pointer('elearning_Customer', customer.objectId)),
        });
        coursePointer = subscriptions[0]?.CoursePtr;
      } catch {
        // A course label is optional; the profile remains useful without it.
      }
    }
    const courseTitle = coursePointer && typeof coursePointer === 'object' ? textValue(coursePointer.CourseTitle, coursePointer.name) : '';
    const profile: LearnerProfile = {
      name,
      initials: profileInitials(name),
      phone: textValue(customer.Mob_no, user.phone, user.mobile),
      email: textValue(customer.Email_id, user.email),
      courseTitle: courseTitle || defaultLearnerProfile.courseTitle,
      avatarUrl: parseFileUrl(customer.ProfileImage ?? customer.ProfileImg ?? customer.Image ?? customer.Photo ?? customer.Avatar ?? user.profileImage),
    };
    profileCache = { userId, value: profile };
    return profile;
  })().finally(() => { profilePromise = undefined; });

  return profilePromise;
}
